import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, FaBuilding, FaUserSecret, FaCalendarAlt, FaSitemap,
  FaPlay, FaClock, FaCheck, FaTimes, FaLightbulb, FaTrophy,
  FaLinkedin, FaTwitter, FaGlobe, FaFileAlt, FaExternalLinkAlt,
  FaHistory, FaMedal, FaSpinner
} from 'react-icons/fa';
import api from '../utils/api';

const OSINT_ICONS = {
  company: FaBuilding,
  personality: FaUserSecret,
  event: FaCalendarAlt,
  organization: FaSitemap
};

const DIFFICULTY_LABELS = {
  1: { label: 'Facile', color: 'text-green-600', bg: 'bg-green-100' },
  2: { label: 'Moyen', color: 'text-yellow-600', bg: 'bg-yellow-100' },
  3: { label: 'Difficile', color: 'text-red-600', bg: 'bg-red-100' }
};

const OSINTPage = () => {
  const [osintTypes, setOsintTypes] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  useEffect(() => {
    loadOSINTTypes();
    loadHistory();
  }, []);

  // Timer
  useEffect(() => {
    let interval;
    if (timerActive) {
      interval = setInterval(() => {
        setTimeSpent(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  const loadOSINTTypes = async () => {
    try {
      const response = await api.get('/osint/types');
      setOsintTypes(response.data.types);
    } catch (error) {
      console.error('Error loading OSINT types:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await api.get('/osint/history');
      setHistory(response.data.attempts);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const startChallenge = async (type, customTopic = null) => {
    setLoading(true);
    setEvaluation(null);
    setAnswers({});
    setTimeSpent(0);
    
    try {
      const response = await api.post('/osint/start', {
        challengeType: type,
        topic: customTopic,
        region: 'FR'
      });
      
      setCurrentChallenge(response.data.challenge);
      setSelectedType(type);
      setTimerActive(true);
    } catch (error) {
      console.error('Error starting challenge:', error);
      alert('Erreur lors du démarrage du défi');
    } finally {
      setLoading(false);
    }
  };

  const submitAnswers = async () => {
    if (!currentChallenge) return;
    
    setLoading(true);
    setTimerActive(false);
    
    try {
      const response = await api.post('/osint/submit', {
        challengeId: currentChallenge.id,
        answers,
        timeSpent
      });
      
      setEvaluation(response.data);
      loadHistory();
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const resetChallenge = () => {
    setCurrentChallenge(null);
    setSelectedType(null);
    setEvaluation(null);
    setAnswers({});
    setTimeSpent(0);
    setTimerActive(false);
  };

  // Sélection du type d'enquête
  if (!currentChallenge && !evaluation) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg">
              <FaSearch className="text-white text-3xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Atelier OSINT</h1>
              <p className="text-gray-600">Investigation en Sources Ouvertes</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <FaHistory /> Historique
          </button>
        </div>

        {/* Explication */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6 mb-8">
          <h2 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
            <FaLightbulb className="text-yellow-500" /> Qu'est-ce que l'OSINT ?
          </h2>
          <p className="text-purple-800">
            L'<strong>OSINT</strong> (Open Source Intelligence) est l'art de collecter des informations 
            à partir de sources publiques : réseaux sociaux, registres officiels, archives de presse, 
            sites web... Une compétence essentielle pour tout journaliste d'investigation.
          </p>
          <p className="text-purple-700 mt-2 text-sm">
            <strong>Comment ça marche ?</strong> Vous recevez un sujet d'enquête. Notre IA a déjà fait 
            ses propres recherches. À vous de retrouver les mêmes informations en un temps limité !
          </p>
        </div>

        {/* Types d'enquêtes */}
        <h2 className="text-xl font-bold text-gray-800 mb-4">Choisissez votre type d'enquête</h2>
        
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {Object.entries(osintTypes).map(([typeId, typeInfo]) => {
            const Icon = OSINT_ICONS[typeId] || FaSearch;
            const difficulty = DIFFICULTY_LABELS[typeInfo.difficulty] || DIFFICULTY_LABELS[1];
            
            return (
              <motion.div
                key={typeId}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => startChallenge(typeId)}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 cursor-pointer hover:border-purple-400 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Icon className="text-purple-600 text-2xl" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800">{typeInfo.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${difficulty.bg} ${difficulty.color}`}>
                        {difficulty.label}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">{typeInfo.description}</p>
                  </div>
                  <FaPlay className="text-purple-400 mt-2" />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Historique */}
        <AnimatePresence>
          {showHistory && history.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-xl border-2 border-gray-200 p-6"
            >
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaTrophy className="text-yellow-500" /> Vos enquêtes précédentes
              </h3>
              <div className="space-y-3">
                {history.slice(0, 5).map((attempt) => (
                  <div key={attempt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800">{attempt.topic}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(attempt.startedAt).toLocaleDateString('fr-FR')} • {attempt.challengeType}
                      </p>
                    </div>
                    <div className="text-right">
                      {attempt.status === 'completed' ? (
                        <span className={`font-bold text-lg ${
                          attempt.percentage >= 70 ? 'text-green-600' :
                          attempt.percentage >= 40 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {attempt.percentage}%
                        </span>
                      ) : (
                        <span className="text-yellow-600 text-sm">En cours</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {loading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 text-center">
              <FaSpinner className="animate-spin text-4xl text-purple-600 mx-auto mb-4" />
              <p className="text-gray-600">Génération de votre enquête OSINT...</p>
              <p className="text-sm text-gray-400 mt-2">Notre IA prépare le défi</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Challenge en cours ou résultats
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header avec timer */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={resetChallenge}
          className="text-gray-500 hover:text-gray-700"
        >
          ← Retour aux enquêtes
        </button>
        
        {!evaluation && (
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-lg ${
            timeSpent > 1800 ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'
          }`}>
            <FaClock />
            {formatTime(timeSpent)}
          </div>
        )}
      </div>

      {/* Résultats */}
      {evaluation ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Score global */}
          <div className={`text-center p-8 rounded-2xl ${
            evaluation.percentage >= 70 ? 'bg-green-100' :
            evaluation.percentage >= 40 ? 'bg-yellow-100' : 'bg-red-100'
          }`}>
            <div className={`text-6xl font-bold mb-2 ${
              evaluation.percentage >= 70 ? 'text-green-600' :
              evaluation.percentage >= 40 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {evaluation.percentage}%
            </div>
            <p className="text-gray-700 font-medium">
              {evaluation.score} / {evaluation.maxScore} points
            </p>
            <p className="text-gray-600 mt-2 text-sm">
              Temps: {formatTime(timeSpent)}
            </p>
          </div>

          {/* Feedback global */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
            <p className="text-purple-900">{evaluation.globalFeedback}</p>
          </div>

          {/* Détail par question */}
          <div className="space-y-4">
            <h3 className="font-bold text-gray-800">Détail des réponses</h3>
            {evaluation.results.map((result, index) => (
              <div 
                key={result.questionId}
                className={`p-4 rounded-xl border-2 ${
                  result.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    result.isCorrect ? 'bg-green-200' : 'bg-red-200'
                  }`}>
                    {result.isCorrect ? <FaCheck className="text-green-600" /> : <FaTimes className="text-red-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{result.question}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Votre réponse:</strong> {result.studentAnswer || '(pas de réponse)'}
                    </p>
                    {result.correctAnswer && (
                      <p className="text-sm text-green-700 mt-1">
                        <strong>Réponse correcte:</strong> {result.correctAnswer}
                        {result.source && <span className="text-gray-500"> (Source: {result.source})</span>}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">{result.feedback}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {result.score} / {result.maxScore} points
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sources révélées */}
          {evaluation.revealedSources?.length > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <h4 className="font-bold text-blue-900 mb-2">Sources utilisées par notre IA</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {evaluation.revealedSources.map((source, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <FaExternalLinkAlt className="text-xs" /> {source}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={resetChallenge}
              className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700"
            >
              Nouvelle enquête
            </button>
          </div>
        </motion.div>
      ) : (
        /* Formulaire de réponses */
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          {/* Briefing */}
          <div className="bg-gray-900 text-white rounded-2xl p-6">
            <div className="flex items-center gap-2 text-yellow-400 mb-2">
              <FaFileAlt /> BRIEFING - CONFIDENTIEL
            </div>
            <h2 className="text-2xl font-bold mb-2">{currentChallenge?.topic}</h2>
            <p className="text-gray-300">{currentChallenge?.briefing}</p>
            {currentChallenge?.context && (
              <p className="text-gray-400 text-sm mt-3 italic">{currentChallenge.context}</p>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 text-sm">
            <p className="text-yellow-800">
              <strong>Mission:</strong> Utilisez toutes les sources ouvertes à votre disposition 
              (Google, LinkedIn, Twitter, Wikipedia, registres officiels...) pour répondre aux questions ci-dessous.
            </p>
          </div>

          {/* Questions */}
          <div className="space-y-4">
            {currentChallenge?.questions?.map((question, index) => (
              <div key={question.id} className="bg-white border-2 border-gray-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-800">{question.question}</p>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">
                        {question.points} pts
                      </span>
                    </div>
                    
                    {question.type === 'url' ? (
                      <div className="flex items-center gap-2">
                        <FaGlobe className="text-gray-400" />
                        <input
                          type="url"
                          placeholder="https://..."
                          value={answers[question.id] || ''}
                          onChange={(e) => setAnswers({...answers, [question.id]: e.target.value})}
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    ) : question.type === 'number' ? (
                      <input
                        type="number"
                        placeholder="Entrez un nombre"
                        value={answers[question.id] || ''}
                        onChange={(e) => setAnswers({...answers, [question.id]: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    ) : question.type === 'date' ? (
                      <input
                        type="text"
                        placeholder="JJ/MM/AAAA ou AAAA"
                        value={answers[question.id] || ''}
                        onChange={(e) => setAnswers({...answers, [question.id]: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    ) : (
                      <textarea
                        placeholder="Votre réponse..."
                        rows={2}
                        value={answers[question.id] || ''}
                        onChange={(e) => setAnswers({...answers, [question.id]: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg resize-none"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit */}
          <button
            onClick={submitAnswers}
            disabled={loading}
            className="w-full py-4 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" /> Évaluation en cours...
              </>
            ) : (
              <>
                <FaCheck /> Soumettre mes réponses
              </>
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default OSINTPage;
